import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  MapPin, 
  Plus, 
  Trash2, 
  Edit3,
  Eye,
  Navigation,
  Building2,
  Loader2,
  Home,
  Factory,
  Warehouse,
  DoorOpen,
  Box
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon options for areas
const AREA_ICONS = {
  building: Building2,
  home: Home,
  factory: Factory,
  warehouse: Warehouse,
  door: DoorOpen,
  box: Box,
  pin: MapPin,
};

// Color options
const AREA_COLORS = [
  { value: '#FFF200', label: 'Safety Yellow' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Green' },
  { value: '#f59e0b', label: 'Orange' },
  { value: '#ef4444', label: 'Red' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#6b7280', label: 'Gray' },
];

export function AreaManager({ 
  sweeps = [],
  currentSweep,
  cameraPosition,
  onNavigateToArea,
  isSDKConnected = false,
}) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newArea, setNewArea] = useState({
    name: '',
    description: '',
    icon: 'building',
    color: '#3b82f6',
    useCurrentPosition: true,
  });

  // Fetch areas from backend
  const fetchAreas = useCallback(async () => {
    if (!token || !currentTour?.id) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/areas/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAreas(data);
      }
    } catch (err) {
      console.error('Failed to fetch areas:', err);
    } finally {
      setLoading(false);
    }
  }, [token, currentTour?.id]);

  useEffect(() => {
    if (token && currentTour?.id) {
      fetchAreas();
    } else {
      setAreas([]);
    }
  }, [token, currentTour?.id, fetchAreas]);

  // Create area
  const handleCreateArea = async () => {
    if (!token || !currentTour?.id || !newArea.name.trim()) return;
    
    setSaving(true);
    try {
      const areaData = {
        name: newArea.name.trim(),
        description: newArea.description.trim(),
        tour_id: currentTour.id,
        icon: newArea.icon,
        color: newArea.color,
        sweep_id: newArea.useCurrentPosition ? currentSweep?.id : null,
        position: newArea.useCurrentPosition && cameraPosition?.position ? {
          x: cameraPosition.position.x,
          y: cameraPosition.position.y,
          z: cameraPosition.position.z,
        } : null,
        rotation: newArea.useCurrentPosition && cameraPosition?.rotation ? {
          x: cameraPosition.rotation.x,
          y: cameraPosition.rotation.y,
          z: cameraPosition.rotation.z || 0,
        } : null,
      };

      const res = await fetch(`${API_URL}/api/areas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(areaData),
      });

      if (res.ok) {
        await fetchAreas();
        setIsCreateDialogOpen(false);
        setNewArea({
          name: '',
          description: '',
          icon: 'building',
          color: '#3b82f6',
          useCurrentPosition: true,
        });
      }
    } catch (err) {
      console.error('Failed to create area:', err);
    } finally {
      setSaving(false);
    }
  };

  // Update area
  const handleUpdateArea = async () => {
    if (!token || !editingArea) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/areas/${editingArea.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editingArea.name,
          description: editingArea.description,
          icon: editingArea.icon,
          color: editingArea.color,
        }),
      });

      if (res.ok) {
        await fetchAreas();
        setEditingArea(null);
      }
    } catch (err) {
      console.error('Failed to update area:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete area
  const handleDeleteArea = async (areaId) => {
    if (!token || !window.confirm('Delete this area?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/areas/${areaId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        await fetchAreas();
      }
    } catch (err) {
      console.error('Failed to delete area:', err);
    }
  };

  // Navigate to area
  const handleNavigateToArea = (area) => {
    if (onNavigateToArea && area.sweep_id) {
      onNavigateToArea(area);
    }
  };

  const IconComponent = AREA_ICONS[newArea.icon] || Building2;
  const EditIconComponent = editingArea ? (AREA_ICONS[editingArea.icon] || Building2) : Building2;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Areas & Zones</span>
          <Badge variant="secondary" className="text-[10px]">
            {areas.length}
          </Badge>
        </div>
        
        {canEdit && (
          <Button 
            variant="outline" 
            size="sm" 
            className="h-7 gap-1"
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={!currentTour?.id || !isSDKConnected}
          >
            <Plus className="h-3 w-3" />
            Add Area
          </Button>
        )}
      </div>

      {/* Viewer mode notice */}
      {isViewer && (
        <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded">
          View-only access. Contact an admin to create or edit areas.
        </div>
      )}

      {/* Areas List */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : areas.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground">
          <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No areas defined</p>
          {canEdit && (
            <p className="text-xs mt-1">Add areas to organize your space</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {areas.map((area) => {
            const AreaIcon = AREA_ICONS[area.icon] || Building2;
            return (
              <div
                key={area.id}
                className="flex items-center gap-2 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors group"
              >
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${area.color}20` }}
                >
                  <AreaIcon className="h-4 w-4" style={{ color: area.color }} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{area.name}</p>
                  {area.description && (
                    <p className="text-xs text-muted-foreground truncate">{area.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {area.sweep_id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleNavigateToArea(area)}
                      title="Go to area"
                    >
                      <Navigation className="h-3 w-3" />
                    </Button>
                  )}
                  
                  {canEdit && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setEditingArea(area)}
                        title="Edit area"
                      >
                        <Edit3 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteArea(area.id)}
                        title="Delete area"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Area Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconComponent className="h-5 w-5" style={{ color: newArea.color }} />
              Create New Area
            </DialogTitle>
            <DialogDescription>
              Define an area or zone in your 3D model
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Area Name *</Label>
              <Input
                placeholder="e.g., Control Room, Pump Station A"
                value={newArea.name}
                onChange={(e) => setNewArea({ ...newArea, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Optional description of this area"
                value={newArea.description}
                onChange={(e) => setNewArea({ ...newArea, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={newArea.icon}
                  onValueChange={(value) => setNewArea({ ...newArea, icon: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(AREA_ICONS).map(([key, Icon]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span className="capitalize">{key}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={newArea.color}
                  onValueChange={(value) => setNewArea({ ...newArea, color: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AREA_COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: color.value }}
                          />
                          <span>{color.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/50">
              <input
                type="checkbox"
                id="useCurrentPosition"
                checked={newArea.useCurrentPosition}
                onChange={(e) => setNewArea({ ...newArea, useCurrentPosition: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="useCurrentPosition" className="text-sm cursor-pointer">
                Link to current camera position
              </Label>
            </div>

            {newArea.useCurrentPosition && currentSweep?.id && (
              <div className="text-xs text-muted-foreground bg-primary/10 p-2 rounded">
                <strong>Position:</strong> Sweep {currentSweep.id.slice(0, 8)}...
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateArea} disabled={saving || !newArea.name.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Area
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Area Dialog */}
      <Dialog open={!!editingArea} onOpenChange={(open) => !open && setEditingArea(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EditIconComponent className="h-5 w-5" style={{ color: editingArea?.color }} />
              Edit Area
            </DialogTitle>
          </DialogHeader>
          
          {editingArea && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Area Name *</Label>
                <Input
                  value={editingArea.name}
                  onChange={(e) => setEditingArea({ ...editingArea, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingArea.description || ''}
                  onChange={(e) => setEditingArea({ ...editingArea, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <Select
                    value={editingArea.icon}
                    onValueChange={(value) => setEditingArea({ ...editingArea, icon: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(AREA_ICONS).map(([key, Icon]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span className="capitalize">{key}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Color</Label>
                  <Select
                    value={editingArea.color}
                    onValueChange={(value) => setEditingArea({ ...editingArea, color: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AREA_COLORS.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: color.value }}
                            />
                            <span>{color.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingArea(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateArea} disabled={saving || !editingArea?.name?.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AreaManager;
